import {
  AgeSegmentationIds,
  AllergySegmentationIds,
  BreedSegmentationIds,
  NutritionSegmentationIds,
} from 'constants/segmentation';
import { usePetProfile } from 'contexts/pet-profile';
import { useTrackingContext } from 'contexts/tracking';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { Action, DraftState, State } from './interface';
import { draftStateReducer, reducer } from './utils';

interface NeedCaptureContextType {
  draftData: DraftState;
  filteredNeedsEntries: Array<[string, NutritionSegmentationIds]>;
  hasInteracted: boolean;
  state: State;
  dispatch: React.Dispatch<Action>;
  handleClose: () => void;
  handleCloseNeeds: () => void;
  handleConfirmNeeds: () => void;
  handleSelectAllergies: (value: AllergySegmentationIds) => void;
  handleSelectNeeds: (value: NutritionSegmentationIds) => void;
  onSelectAge: (value: AgeSegmentationIds) => void;
  onSelectBreed: (value: BreedSegmentationIds) => void;
}

const NeedCaptureContext = createContext<NeedCaptureContextType>(
  {} as NeedCaptureContextType
);

/**
 * Provider for the need capture context.
 */
export const NeedCaptureProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const { petProfile, setPetProfile } = usePetProfile();
  const { trackHotjarEvent } = useTrackingContext();

  const initialState = {
    isAgeSelectorOpen: false,
    isBreedSelectorOpen: false,
    isNeedSelectorOpen: false,
    selectedAge: petProfile?.selectedAge,
    selectedBreed: petProfile?.selectedBreed,
    selectedNeeds: petProfile?.selectedNeeds ?? [],
    selectedAllergies: petProfile?.selectedAllergies ?? [],
    lastTouched: petProfile?.lastTouched,
  };

  const filteredNeedsEntries = Object.entries(NutritionSegmentationIds).filter(
    ([, value]) =>
      value !== NutritionSegmentationIds.Disease &&
      value !== NutritionSegmentationIds.Foodaid &&
      value !== NutritionSegmentationIds.NoHelpNeeded
  );

  const [state, dispatch] = useReducer(reducer, initialState);

  const needsAlleriesDraft: DraftState = {
    draftNeeds: state?.selectedNeeds ?? [],
    draftAllergies: state?.selectedAllergies ?? [],
  };

  const [draftData, dispatchTemporaryData] = useReducer(
    draftStateReducer,
    needsAlleriesDraft
  );

  const handleSelectNeeds = useCallback(
    (value: NutritionSegmentationIds): void => {
      if (draftData.draftNeeds.includes(value)) {
        const newNeeds = draftData.draftNeeds.filter((item) => item !== value);
        dispatchTemporaryData({
          type: 'SET_DRAFT_NEEDS',
          payload: newNeeds,
        });

        if (value === NutritionSegmentationIds.Intolerance) {
          dispatchTemporaryData({
            type: 'SET_DRAFT_ALLERGIES',
            payload: [],
          });
        }
      } else {
        dispatchTemporaryData({
          type: 'SET_DRAFT_NEEDS',
          payload: [...draftData.draftNeeds, value],
        });
      }
    },
    [draftData.draftNeeds]
  );

  const onSelectAge = useCallback(
    (value: AgeSegmentationIds): void => {
      setHasInteracted(true);
      dispatch({ type: 'SET_AGE', payload: value });
      setPetProfile({
        ...petProfile,
        ...(petProfile.selectedAge !== value && { lastTouched: value }),
        selectedAge: petProfile.selectedAge === value ? undefined : value,
      });
      dispatch({ type: 'TOGGLE_AGE_SELECTOR' });
    },
    [petProfile, setPetProfile]
  );

  const onSelectBreed = useCallback(
    (value: BreedSegmentationIds): void => {
      setHasInteracted(true);
      dispatch({ type: 'SET_BREED', payload: value });
      setPetProfile({
        ...petProfile,
        ...(petProfile.selectedBreed !== value && { lastTouched: value }),
        selectedBreed: petProfile.selectedBreed === value ? undefined : value,
      });
      dispatch({ type: 'TOGGLE_BREED_SELECTOR' });
    },
    [petProfile, setPetProfile]
  );

  const handleSelectAllergies = useCallback(
    (value: AllergySegmentationIds): void => {
      if (draftData.draftAllergies.includes(value)) {
        dispatchTemporaryData({
          type: 'SET_DRAFT_ALLERGIES',
          payload: draftData.draftAllergies.filter((item) => item !== value),
        });
      } else {
        dispatchTemporaryData({
          type: 'SET_DRAFT_ALLERGIES',
          payload: [...draftData.draftAllergies, value],
        });
      }
    },
    [draftData.draftAllergies]
  );

  const handleCloseNeeds = useCallback(() => {
    dispatchTemporaryData({
      type: 'SET_DRAFT_NEEDS',
      payload: state?.selectedNeeds ?? [],
    });
    dispatchTemporaryData({
      type: 'SET_DRAFT_ALLERGIES',
      payload: state?.selectedAllergies ?? [],
    });
    dispatch({ type: 'TOGGLE_NEED_SELECTOR' });
  }, [state?.selectedNeeds, state?.selectedAllergies]);

  const handleConfirmNeeds = useCallback(() => {
    setHasInteracted(true);
    dispatch({
      type: 'SET_NEEDS',
      payload: draftData.draftNeeds,
    });
    dispatch({
      type: 'SET_ALLERGIES',
      payload: draftData.draftAllergies,
    });
    setPetProfile({
      ...petProfile,
      selectedNeeds: draftData.draftNeeds,
      selectedAllergies: draftData.draftAllergies,
      lastTouched: draftData.lastTouched,
    });
    dispatch({ type: 'TOGGLE_NEED_SELECTOR' });
  }, [
    draftData.draftAllergies,
    draftData.draftNeeds,
    draftData.lastTouched,
    petProfile,
    setPetProfile,
  ]);

  const handleClose = useCallback(() => {
    if (state.isAgeSelectorOpen) dispatch({ type: 'TOGGLE_AGE_SELECTOR' });
    if (state.isBreedSelectorOpen) dispatch({ type: 'TOGGLE_BREED_SELECTOR' });
    if (state.isNeedSelectorOpen) handleCloseNeeds();
  }, [
    state.isAgeSelectorOpen,
    state.isBreedSelectorOpen,
    state.isNeedSelectorOpen,
    handleCloseNeeds,
  ]);

  // Track Hotjar event when user has interacted with the NeedCapture component
  useEffect(() => {
    if (hasInteracted) trackHotjarEvent();
  }, [hasInteracted, trackHotjarEvent]);

  const value = useMemo(() => {
    return {
      draftData,
      filteredNeedsEntries,
      hasInteracted,
      state,
      dispatch,
      handleClose,
      handleCloseNeeds,
      handleConfirmNeeds,
      handleSelectAllergies,
      handleSelectNeeds,
      onSelectAge,
      onSelectBreed,
    };
  }, [
    draftData,
    filteredNeedsEntries,
    hasInteracted,
    state,
    handleClose,
    handleCloseNeeds,
    handleConfirmNeeds,
    handleSelectAllergies,
    handleSelectNeeds,
    onSelectAge,
    onSelectBreed,
  ]);

  return (
    <NeedCaptureContext.Provider value={value}>
      {children}
    </NeedCaptureContext.Provider>
  );
};

export const useNeedCapture = (): NeedCaptureContextType =>
  useContext(NeedCaptureContext);
